<script setup>
import { reactive, onMounted, onUnmounted, computed } from 'vue';
import Header from './components/Header.vue';
import { RouterView } from 'vue-router';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import RingBarsSpinner from 'vue-global-loader/RingBarsSpinner.vue';
import { useProductStore } from '@/stores/productsStore';
import { useAuthStore } from '@/stores/authStore';
import { useQzStore } from '@/stores/qzStore';

const authStore = useAuthStore();
const store = useProductStore();
const qzStore = useQzStore();
const password = reactive({ value: '' });

// Function to extract query parameters from the URL
const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('Full URL:', window.location.href);
    console.log('URLP', urlParams);

    // Check if branchID and userId are in the query parameters
    const branchID = urlParams.get('branchID');
    const userId = urlParams.get('userId');

    // If query parameters are missing, check localStorage
    if (!branchID || !userId) {
        const storedBranchId = localStorage.getItem('branchId');
        const storedUserId = localStorage.getItem('userId');

        if (storedBranchId && storedUserId) {
            console.log('Using branchId and userId from localStorage');
            return {
                branchID: storedBranchId,
                userId: storedUserId,
            };
        }
    }

    // Return query parameters if they exist
    return {
        branchID,
        userId,
    };
};

// Define the initial link based on query parameters or localStorage
const { branchID, userId } = getQueryParams();
const initialLink = branchID && userId ? `branchID=${branchID}&userId=${userId}` : null;

console.log('Initial Link', initialLink);

onMounted(async () => {
    if (initialLink) {
        await store.initialize(initialLink);

        // If pairing is already done, directly fetch products
        if (store.pairingDone) {
            await store.fetchProducts();
        } else {
            // If pairing is not done, show the pairing form
            console.log('Branch not paired. Showing pairing form.');
        }
    }
});

// Function to handle Enter key press
const handleEnterKeyPress = (event) => {
    if (event.key === 'Enter') {
        store.pairBranch(password.value);
    }
};

// Force Logout
let intervalId;

const checkTokenExpiry = () => {
  if (authStore.token && authStore.isTokenExpired(authStore.token)) {
    authStore.logout('Cashier');
  }

  if (authStore.managerToken && authStore.isTokenExpired(authStore.managerToken)) {
    authStore.logout('Manager');
  }
};

// Computed property to check if QZ is disabled by user
const isQzDisabledByUser = computed(() => {
  return !qzStore.isQzConnected && localStorage.getItem('useQzTrayPrint') === 'false';
});

onMounted(() => {
  checkTokenExpiry();
  
  // Only connect to QZ if user wants QZ printing and not already connecting
  const savedPreference = localStorage.getItem("useQzTrayPrint");
  // Connect only if there is an explicit true in localStorage
  const userWantsQzPrint = savedPreference === "true";
  
  if (userWantsQzPrint && !qzStore.isConnecting) {
    qzStore.connectQZ(true);
  }
  
  qzStore.initQZSecurity();

  intervalId = setInterval(() => {
    checkTokenExpiry();
  }, 60000);
});

onUnmounted(() => {
  clearInterval(intervalId);
});

</script>

<template>
    <section class="h-screen px-4 relative">
        <div class="container-xl lg:container m-auto">
            <!-- Show "You need to pair a branch" if no query params and no localStorage data -->
            <div v-if="!initialLink" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <p class="text-lg">You need to pair a branch!</p>
            </div>

            <!-- Show Branch Pairing Form if not paired and query parameters exist -->
            <div v-else-if="!store.pairingDone && initialLink" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <!-- Password input -->
                <input
                    v-model="password.value"
                    type="password"
                    placeholder="Enter password"
                    class="form-input"
                    @keydown="handleEnterKeyPress"
                />
                <button @click="store.pairBranch(password.value)" class="p-2 bg-purple-700 text-white rounded mt-4">Pair Branch</button>
            </div>

            <!-- Show Loading Spinner while loading is true -->
            <div v-else-if="store.isLoading" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <RingBarsSpinner style="width: 60px;" />
                <p class="mt-4">Loading... {{ store.loadingPercentage }}%</p>
            </div>

            <!-- Product listing when done loading -->
            <div v-else class="">
                <div class="h-screen flex flex-col">
                    <Header />
                    <RouterView />
                </div>
            </div>

            <div class="fixed bottom-4 left-4 z-50">
                    <div v-if="qzStore.isConnecting" class="bg-yellow-500 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                        <i class="pi pi-spinner pi-spin"></i>
                        <span class="text-sm font-medium">QZ print connecting</span>
                    </div>
                    <div v-else-if="isQzDisabledByUser && !qzStore.isConnecting" class="bg-gray-600 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                        <i class="pi pi-times-circle"></i>
                        <span class="text-sm font-medium">QZ tray is not connected</span>
                    </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.form-input {
    width: 300px;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    outline: none;
}

.form-input:focus {
    border-color: #7c3aed; 
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}

.iti {
  --iti-path-flags-1x: url('path/to/flags.webp');
  --iti-path-flags-2x: url('path/to/flags@2x.webp');
  --iti-path-globe-1x: url('path/to/globe.webp');
  --iti-path-globe-2x: url('path/to/globe@2x.webp');
}
</style>